import { Storybook } from "@tento-ui/ui/storybook";
import { Button } from "@tento-ui/components/button/button";
import { Overlay } from "@tento-ui/components/overlay";
import { Toast } from "@tento-ui/components/toast/toast";
import { CreateSchedulingLinkForm } from "./create-scheduling-link-form";
import { SchedulingLinks } from "@tento-lona";
import { Utc } from "@tento-chrono";
import { Actor, type JsonObject, Patch, RowNetworkKey, UserId } from "@tento-lona/sheets";
import type { SchedulingLinkStore } from "./scheduling-link-store";

const MOCK_OWNER_ID = UserId.of("user_abc123");

/** A mock link's attributes, decoded from the bag a stored link row carries. */
export function mockLinkAttributes(bag: JsonObject): SchedulingLinks.Attributes {
  const attributes = SchedulingLinks.Attributes.decode(bag);
  if (attributes == null) throw new Error("mock scheduling-link attributes do not decode");
  return attributes;
}

/**
 * An in-memory scheduling-links store for the storybooks: the surface the
 * sidebar and the form use (`SchedulingLinkStore`), nothing sent.
 */
export class MemorySchedulingLinkStore implements SchedulingLinkStore {
  #links: SchedulingLinks.Row[];

  constructor(links: SchedulingLinks.Row[]) {
    this.#links = links;
  }

  ensureLoaded(): Promise<void> {
    return Promise.resolve();
  }

  peekAll(): SchedulingLinks.Row[] {
    return [...this.#links];
  }

  create(
    request: SchedulingLinks.CreateRequest,
    _actor: Actor,
  ): Promise<SchedulingLinks.Row> {
    const now = Utc.now();
    const row = new SchedulingLinks.Row({
      id: RowNetworkKey.fromWire(crypto.randomUUID()),
      ownerId: MOCK_OWNER_ID,
      label: request.title,
      lookupKey: `:bookings:${request.slug}`,
      attributes: {
        slug: request.slug,
        description: request.description,
        ownerName: request.ownerName,
        durationMinutes: request.durationMinutes,
        timezone: request.timezone,
        availability: [...request.availability],
        isActive: true,
        createdAt: now,
        updatedAt: now,
        renderMode: null,
      },
    });
    this.#links.push(row);
    return Promise.resolve(row);
  }

  update(
    link: SchedulingLinks.Row,
    request: SchedulingLinks.Patch,
    _actor: Actor,
  ): Promise<SchedulingLinks.Row> {
    const existing = this.#links.find((l) => l.id === link.id)!;
    const updated = new SchedulingLinks.Row({
      id: existing.id,
      ownerId: existing.ownerId,
      label: request.title ?? existing.label,
      lookupKey: existing.lookupKey,
      attributes: {
        ...existing.attributes,
        description:
          request.description === undefined
            ? existing.attributes.description
            : Patch.wire(request.description),
        durationMinutes:
          request.durationMinutes ?? existing.attributes.durationMinutes,
        timezone: request.timezone ?? existing.attributes.timezone,
        availability: request.availability != null ? [...request.availability] : existing.attributes.availability,
        isActive: request.isActive ?? existing.attributes.isActive,
        updatedAt: Utc.now(),
      },
    });
    const idx = this.#links.findIndex((l) => l.id === link.id);
    if (idx !== -1) this.#links[idx] = updated;
    return Promise.resolve(updated);
  }
}

function makeMockLink(): SchedulingLinks.Row {
  const now = new Date().toISOString();
  return new SchedulingLinks.Row({
    id: RowNetworkKey.fromWire(crypto.randomUUID()),
    ownerId: MOCK_OWNER_ID,
    label: "Coffee Chat",
    lookupKey: ":bookings:coffee-chat",
    attributes: mockLinkAttributes({
      slug: "coffee-chat",
      durationMinutes: 30,
      timezone: "America/New_York",
      description: "A casual chat over coffee",
      availability: [],
      isActive: true,
      createdAt: now,
      updatedAt: now,
    }),
  });
}

export class CreateSchedulingLinkFormStoryBook extends Storybook.Default {
  title(): string {
    return "Create Scheduling Link Form";
  }

  examples(): Storybook.Example[] {
    return [
      {
        title: "Default",
        render: () => {
          const store = new MemorySchedulingLinkStore([]);
          const $form = CreateSchedulingLinkForm.make();
          $form.assignStyles({ maxWidth: "480px", width: "100%" });
          $form.bind({
            store,
            onSaved: (link) => {
              Toast.info(`Created: ${link.label} (/${link.attributes.slug})`);
            },
            onDismiss: () => {
              console.log("Dismissed");
            },
          });
          return $form;
        },
      },
      {
        title: "Edit Mode",
        render: () => {
          const mockLink = makeMockLink();
          const store = new MemorySchedulingLinkStore([mockLink]);
          const $form = CreateSchedulingLinkForm.make();
          $form.assignStyles({ maxWidth: "480px", width: "100%" });
          $form.bind({
            store,
            link: mockLink,
            onSaved: (link) => {
              Toast.info(`Updated: ${link.label}`);
            },
            onDismiss: () => {
              console.log("Dismissed");
            },
          });
          return $form;
        },
      },
      {
        title: "Edit Mode (Inline)",
        render: () => {
          const mockLink = makeMockLink();
          const store = new MemorySchedulingLinkStore([mockLink]);
          const $form = CreateSchedulingLinkForm.make();
          $form.toggleAttribute("inline", true);
          $form.assignStyles({ maxWidth: "480px", width: "100%" });
          $form.bind({
            store,
            link: mockLink,
            onSaved: (link) => {
              Toast.info(`Updated: ${link.label}`);
            },
            onDismiss: () => {
              console.log("Dismissed");
            },
          });
          return $form;
        },
      },
      {
        title: "Show as Modal",
        render: () => {
          return Button.makeWithLight("New Scheduling Link", {
            type: "primary",
            onClick: () => {
              const store = new MemorySchedulingLinkStore([]);
              const $form = CreateSchedulingLinkForm.make();
              $form.assignStyles({ maxWidth: "480px" });
              $form.bind({
                store,
                onSaved: (link) => {
                  Toast.info(
                    `Created: ${link.label} (/${link.attributes.slug})`,
                  );
                  Overlay.dismiss($form);
                },
                onDismiss: () => {
                  Overlay.dismiss($form);
                },
              });
              Overlay.reveal($form);
            },
          });
        },
      },
    ];
  }
}
